import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CreditCard, CalendarClock, Sparkles, AlertTriangle, CheckCircle2, ExternalLink, Loader2, ArrowRight, Users, HardDrive } from 'lucide-react';
import { api } from '../lib/api';
import { redirectExternal } from '../lib/redirect';
import ManualOpenModal from './ManualOpenModal';

/**
 * Iter 125 — Live subscription-status widget for the Admin Dashboard.
 * Iter 126 — Added inline seats/storage usage chip (from /clinic/me/usage)
 *            and a manual-open modal for iframes that block `_blank`.
 *
 * Renders only for the clinic owner (the `/clinic/me/subscription` endpoint
 * gates on `require_owner`). Non-owner admins and clinicians silently get
 * nothing here — no ugly "You can't see this" chrome on the dashboard.
 *
 * Displays: plan badge, price/cycle, subscription status pill, next
 * billing date (or trial-ends countdown), an inline usage chip
 * ("8/15 seats · 4.2 GB / 25 GB"), and a primary "Manage in Stripe" button
 * that opens the Customer Portal via `redirectExternal` to survive iframe
 * embedding. A small "or open billing page →" link routes back to the
 * in-app `/admin/billing` view for people who want the internal UI.
 */
function fmtDate(iso) {
  if (!iso) return null;
  try {
    let d;
    if (typeof iso === 'number') d = new Date(iso * 1000);
    else if (/^\d+$/.test(String(iso))) d = new Date(parseInt(iso, 10) * 1000);
    else d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    let d;
    if (typeof iso === 'number') d = new Date(iso * 1000);
    else if (/^\d+$/.test(String(iso))) d = new Date(parseInt(iso, 10) * 1000);
    else d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

function fmtBytes(bytes) {
  if (!bytes || bytes < 0) return '0 B';
  const gb = 1024 ** 3;
  const mb = 1024 ** 2;
  if (bytes >= gb) return `${(bytes / gb).toFixed(bytes >= 10 * gb ? 0 : 1)} GB`;
  if (bytes >= mb) return `${(bytes / mb).toFixed(0)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

const STATUS_META = {
  trialing:            { label: 'Trial',    tone: 'bg-[#FFB000]/15 text-[#8A5A00] border-[#FFB000]/30', dot: '#FFB000' },
  active:              { label: 'Active',   tone: 'bg-[#1B5E20]/10 text-[#1B5E20] border-[#5DBB66]/40', dot: '#1B5E20' },
  past_due:            { label: 'Past due', tone: 'bg-[#B71C1C]/10 text-[#B71C1C] border-[#B71C1C]/30', dot: '#B71C1C' },
  unpaid:              { label: 'Unpaid',   tone: 'bg-[#B71C1C]/10 text-[#B71C1C] border-[#B71C1C]/30', dot: '#B71C1C' },
  canceled:            { label: 'Cancelled', tone: 'bg-fg-muted/10 text-fg-muted border-soft', dot: '#8A8A8A' },
  incomplete:          { label: 'Incomplete', tone: 'bg-fg-muted/10 text-fg-muted border-soft', dot: '#8A8A8A' },
  incomplete_expired:  { label: 'Expired',   tone: 'bg-fg-muted/10 text-fg-muted border-soft', dot: '#8A8A8A' },
};

/**
 * Tiny inline progress bar used inside the usage chip. Keeps the pill
 * compact while still conveying the pct visually.
 */
function UsageBar({ pct, warn }) {
  const w = Math.max(2, Math.min(100, Math.round(pct * 100)));
  return (
    <span className="inline-block w-8 h-1 rounded-full bg-current/10 overflow-hidden align-middle ml-0.5" style={{ background: warn ? '#B71C1C22' : '#8A8A8A22' }}>
      <span className="block h-full rounded-full" style={{ width: `${w}%`, background: warn ? '#B71C1C' : '#003D80' }} />
    </span>
  );
}

export default function SubscriptionWidget() {
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [manualUrl, setManualUrl] = useState(null);

  useEffect(() => {
    let alive = true;
    // Fetch subscription + usage in parallel. Usage is optional — if the
    // endpoint fails we just hide the chip, we don't fail the whole widget.
    Promise.allSettled([
      api.get('/clinic/me/subscription'),
      api.get('/clinic/me/usage'),
    ]).then(([sub, us]) => {
      if (!alive) return;
      if (sub.status === 'fulfilled') {
        setData(sub.value.data);
      } else if (sub.reason?.response?.status === 403) {
        setForbidden(true);
      }
      if (us.status === 'fulfilled') setUsage(us.value.data?.usage || null);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/clinic/me/subscription/portal', {});
      const url = data?.portal_url;
      if (!url) {
        toast.error('Stripe did not return a portal URL. Try the Billing page.');
        return;
      }
      const ok = redirectExternal(url);
      if (!ok) {
        // Iframe sandbox blocked _blank — fall back to a modal with a
        // native <a target="_top"> link that the user can click.
        setManualUrl(url);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (forbidden) return null;
  if (loading) {
    return (
      <div className="admin-card mb-6 flex items-center gap-3 text-sm text-fg-muted" data-testid="sub-widget-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading subscription…
      </div>
    );
  }
  if (!data) return null;

  const status = data.subscription_status || 'trialing';
  const meta = STATUS_META[status] || STATUS_META.canceled;
  const isTrial = status === 'trialing';
  const nextDate = isTrial ? data.trial_ends_at : data.stripe_current_period_end;
  const dateLabel = fmtDate(nextDate);
  const daysLeft = daysUntil(nextDate);
  const trialUrgent = isTrial && typeof daysLeft === 'number' && daysLeft <= 7;
  const amount = data.amount_eur;
  const cycle = data.billing === 'yearly' ? 'year' : 'month';
  const planLabel = data.plan_label || (data.plan || 'solo').replace(/^./, c => c.toUpperCase());
  const hasPortal = !!data.has_stripe_customer;

  // Usage chip data.
  const seats = usage ? `${usage.seats_used}/${usage.seats_max === 999 ? '∞' : usage.seats_max}` : null;
  const storage = usage ? `${fmtBytes(usage.storage_bytes)} / ${fmtBytes(usage.storage_max_bytes)}` : null;
  const seatPct = usage ? Math.min(1, usage.seat_pct || 0) : 0;
  const storagePct = usage ? Math.min(1, usage.storage_pct || 0) : 0;
  const seatWarn = seatPct >= 0.8;
  const storageWarn = storagePct >= 0.8;

  // What the primary action should be:
  const primary = (() => {
    if (status === 'active' || status === 'past_due' || status === 'unpaid') {
      return hasPortal
        ? { label: 'Manage in Stripe', onClick: openPortal, external: true }
        : { label: 'Open billing', to: '/admin/billing' };
    }
    if (status === 'canceled') return { label: 'Reactivate plan', to: '/admin/billing' };
    return { label: 'Choose a plan', to: '/admin/billing' };
  })();

  return (
    <>
      <div
        className="admin-card mb-8 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #FFFDF7 0%, #FFF4E0 100%)',
          borderColor: trialUrgent ? '#FFB00055' : undefined,
        }}
        data-testid="sub-widget"
      >
        {/* Decorative accent — subtle sparkle to hint this is the paid tier. */}
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-20" style={{ background: '#FFB000' }}/>
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
          {/* Left — plan + status */}
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="w-11 h-11 shrink-0 rounded-xl bg-brand/10 text-brand flex items-center justify-center">
              <CreditCard className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.18em] text-fg-muted font-semibold">Subscription</div>
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${meta.tone}`}
                  data-testid="sub-widget-status"
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: meta.dot }} />
                  {meta.label}
                </span>
              </div>
              <div className="font-heading font-bold text-2xl leading-tight mt-1" data-testid="sub-widget-plan">
                {planLabel}
                {amount != null && (
                  <span className="text-base font-medium text-fg-muted ml-2">
                    · €{amount}/{cycle}
                  </span>
                )}
              </div>
              {/* Next billing / trial-ends line */}
              <div className="text-xs text-fg-muted mt-1 inline-flex items-center gap-1.5" data-testid="sub-widget-next">
                <CalendarClock className="w-3.5 h-3.5" />
                {isTrial && dateLabel && (
                  <span>
                    Trial ends <strong>{dateLabel}</strong>
                    {typeof daysLeft === 'number' && daysLeft >= 0 && (
                      <span className={trialUrgent ? ' text-[#B71C1C] font-semibold' : ' text-fg-muted'}>
                        {' '}· {daysLeft} day{daysLeft === 1 ? '' : 's'} left
                      </span>
                    )}
                  </span>
                )}
                {!isTrial && dateLabel && status === 'active' && (
                  <span>Next billing on <strong>{dateLabel}</strong></span>
                )}
                {!isTrial && dateLabel && status === 'canceled' && (
                  <span>Access ends <strong>{dateLabel}</strong></span>
                )}
                {!isTrial && dateLabel && (status === 'past_due' || status === 'unpaid') && (
                  <span className="text-[#B71C1C] font-medium">Payment failed — retry by <strong>{dateLabel}</strong></span>
                )}
                {!dateLabel && (
                  <span>No billing date on file yet.</span>
                )}
              </div>

              {/* Iter 126 — Usage chip: seats + storage inline. */}
              {usage && (
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="sub-widget-usage">
                  <span
                    className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full border ${
                      seatWarn
                        ? 'bg-[#B71C1C]/10 text-[#B71C1C] border-[#B71C1C]/30'
                        : 'bg-bg-secondary/60 text-fg-muted border-soft'
                    }`}
                    title={`${usage.seats_used} seats used of ${usage.seats_max === 999 ? 'unlimited' : usage.seats_max} allowed`}
                    data-testid="sub-widget-seats-chip"
                  >
                    <Users className="w-3 h-3"/>
                    <span className="font-semibold" data-testid="sub-widget-seats">{seats}</span>
                    <span>seats</span>
                    <UsageBar pct={seatPct} warn={seatWarn}/>
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full border ${
                      storageWarn
                        ? 'bg-[#B71C1C]/10 text-[#B71C1C] border-[#B71C1C]/30'
                        : 'bg-bg-secondary/60 text-fg-muted border-soft'
                    }`}
                    title={`${fmtBytes(usage.storage_bytes)} used of ${fmtBytes(usage.storage_max_bytes)} allowed`}
                    data-testid="sub-widget-storage-chip"
                  >
                    <HardDrive className="w-3 h-3"/>
                    <span className="font-semibold" data-testid="sub-widget-storage">{storage}</span>
                    <UsageBar pct={storagePct} warn={storageWarn}/>
                  </span>
                  {(seatWarn || storageWarn) && (
                    <Link
                      to="/admin/billing"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#B71C1C] hover:underline"
                      data-testid="sub-widget-upgrade-link"
                    >
                      Upgrade → more room
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right — primary action */}
          <div className="flex flex-col items-stretch lg:items-end gap-1.5 shrink-0">
            {primary.onClick ? (
              <button
                type="button"
                onClick={primary.onClick}
                disabled={busy}
                className="btn-primary text-sm inline-flex items-center justify-center gap-2 min-w-[180px] disabled:opacity-60"
                data-testid="sub-widget-manage"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>
                  {primary.label}
                  {primary.external ? <ExternalLink className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                </>}
              </button>
            ) : (
              <Link
                to={primary.to}
                className="btn-primary text-sm inline-flex items-center justify-center gap-2 min-w-[180px]"
                data-testid="sub-widget-manage"
              >
                {primary.label} <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <Link
              to="/admin/billing"
              className="text-[11px] text-fg-muted hover:text-brand transition inline-flex items-center gap-1 justify-center lg:justify-end"
              data-testid="sub-widget-billing-link"
            >
              {primary.onClick ? 'or open billing page' : 'or manage in Stripe'} →
            </Link>
          </div>
        </div>

        {/* Contextual footer for past-due / canceled — gentle nudge */}
        {(status === 'past_due' || status === 'unpaid') && (
          <div className="relative mt-3 pt-3 border-t border-[#B71C1C]/20 text-xs text-[#B71C1C] inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Update your payment method to avoid losing access.
          </div>
        )}
        {status === 'active' && (
          <div className="relative mt-3 pt-3 border-t border-soft text-[11px] text-fg-muted inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#1B5E20]" />
            Your subscription renews automatically. Cancel any time from Stripe.
          </div>
        )}
        {isTrial && trialUrgent && (
          <div className="relative mt-3 pt-3 border-t border-[#FFB000]/30 text-[11px] text-[#8A5A00] inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            Your trial is ending soon — pick a plan to keep your data live.
          </div>
        )}
      </div>

      <ManualOpenModal url={manualUrl} onClose={() => setManualUrl(null)} />
    </>
  );
}
